/* eslint @typescript-eslint/strict-boolean-expressions: off */

// Styling
import styles from "./UserDetails.module.css";
import cn from "classnames";

// Components
import Row from "gui/layout/row";
import { Icon } from "gui/icon/icon";
import Link from "next/link";
import { Avatar } from "gui/avatar/avatar";
import Linkify from "react-linkify";

// State
import { useAnalytics } from "gui/analytics/analytics";
import CopyToClipboardButton from "gui/CopyToClipboardButton/CopyToClipboardButton";
import { useMemo } from "react";
import {
  arrowBelowCenteredOnTrigger,
  centeredBelow,
  TooltipOld,
} from "components/TooltipOld/TooltipOld";
import { useListUserTeamsQuery } from "lib/types/graphql";
import { gql } from "@apollo/client";

interface UserFormObject {
  twitter_handle?: string | null;
  telegram_handle?: string | null;
  discord_username?: string | null;
  bio?: string | null;
  is_available_for_gigs?: boolean | null;
  ethereum_address?: string | null;
}

interface Props {
  savedDetails: UserFormObject;
  userId: number;
  className?: string;
}

export default function UserDetails(props: Props) {
  const { captureEvent } = useAnalytics();

  return (
    <div className={cn(styles["user-details"], props.className)}>
      {props.savedDetails.bio && (
        <Row gap="small" layout="start-aligned" className={styles.bio}>
          <Icon icon="book" aria-hidden className={styles.icon} />
          <span>
            <Linkify
              componentDecorator={(href, text) => (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  {text}
                </a>
              )}
            >
              {props.savedDetails.bio}
            </Linkify>
          </span>
        </Row>
      )}
      {props.savedDetails.twitter_handle && (
        <Row gap="small">
          <Icon icon="twitter" className={styles.icon} aria-hidden />
          <a
            target="_blank"
            aria-label="Twitter"
            href={`https://twitter.com/${props.savedDetails.twitter_handle}`}
            className={styles["user-details-item"]}
            onClick={() => {
              captureEvent("User profile Twitter link clicked");
            }}
          >
            @{props.savedDetails.twitter_handle}
          </a>
        </Row>
      )}
      {props.savedDetails.ethereum_address && (
        <EthereumAddress address={props.savedDetails.ethereum_address} />
      )}
      {props.savedDetails.telegram_handle && (
        <Row gap="small">
          <Icon icon="telegram" aria-hidden />
          <a
            target="_blank"
            aria-label="Telegram"
            href={`https://t.me/${props.savedDetails.telegram_handle}`}
            className={styles["user-details-item"]}
            onClick={() => {
              captureEvent("User profile Telegram link clicked");
            }}
          >
            @{props.savedDetails.telegram_handle}
          </a>
        </Row>
      )}
      {props.savedDetails.discord_username && (
        <Row gap="small">
          <Icon icon="discord" aria-hidden />
          <span aria-label="Discord" className={styles["user-details-item"]}>
            {props.savedDetails.discord_username}
          </span>
        </Row>
      )}
      {props.savedDetails.is_available_for_gigs && (
        <Row gap="small">
          <Icon icon="wizard" aria-hidden />
          <span className={styles["user-details-item"]}>
            Wizard taking gigs
          </span>
        </Row>
      )}
      <Teams userId={props.userId} />
    </div>
  );
}

export function shortenAddress(address: string): string | undefined {
  if (address.length !== 42) {
    return undefined;
  }
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

const EthereumAddress: React.FC<{ address: string }> = (props) => {
  const parsedAddress = useMemo(() => shortenAddress(props.address), [
    props.address,
  ]);

  if (!parsedAddress) {
    return null;
  }

  return (
    <Row gap="small">
      <Icon icon="ethereum" className={styles.icon} aria-hidden />
      <TooltipOld
        label={props.address}
        color="black"
        className={styles.tooltip}
        position={centeredBelow}
        trianglePosition={arrowBelowCenteredOnTrigger}
      >
        <p
          aria-label="Ethereum Address"
          className={styles["user-details-item"]}
        >
          {parsedAddress}
        </p>
      </TooltipOld>
      <CopyToClipboardButton text={props.address} className={styles.copyIcon} />
    </Row>
  );
};

const Teams = (props: { userId: number }) => {
  const { data } = useListUserTeamsQuery({
    variables: { user_id: props.userId },
  });

  if (!data || data.teams.length === 0) {
    return null;
  }

  return (
    <>
      <Row gap="small" layout="start-aligned" className={styles.teamsRow}>
        <Icon icon="people" aria-hidden className={styles.icon} />
        <span>Teams</span>
      </Row>
      <Row gap="small" layout="start-aligned" className={styles.teamsRow}>
        <div className={styles.teams}>
          {data.teams.map((t) => (
            <Link key={t.handle} href={`/${t.handle}`}>
              <a>
                <TooltipOld
                  label={t.name}
                  color="black"
                  position={centeredBelow}
                  trianglePosition={arrowBelowCenteredOnTrigger}
                >
                  <span>
                    <Avatar src={t.profile_image_url || undefined} size={20} />
                  </span>
                </TooltipOld>
              </a>
            </Link>
          ))}
        </div>
      </Row>
    </>
  );
};

gql`
  query ListUserTeams($user_id: Int!) {
    teams(where: { memberships: { user: { id: { _eq: $user_id } } } }) {
      name
      handle
      profile_image_url
    }
  }
`;
